import {
  aliasFormationFaults, aliasKind, aliasMembersExclusive, aliasStatus, aliasUncoveredFrom, answerRefEligibility, answerRefStatus, collectAliasUsages, membersWithout, sameRef,
  type AliasFault, type AliasUsage, type AnswerRef, type AnswerRefAlias,
} from '@agavi/blocks';
import { AlertTriangle, ChevronDown, ChevronRight, MessageSquareText, OctagonAlert, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { aliasDisplayName, answerRefAddress, answerRefFacetSuffix, useAnswerRefScope } from './answer-ref-context';
import { AnswerRefPickerMenu, answerRefSections } from './answer-ref-picker-menu';
import { BLOCK_META } from './block-library';
import { useBlockIdentity } from './fired-states';

import type { Task } from '@/types/experience';

import { ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
// Shared with the chip's own rename, so the two cannot disagree about when a name is saved.
import { CommitField } from '@/components/ui/commit-field';
import { IconButton } from '@/components/ui/icon-button';
import { useCapabilities } from '@/hooks/use-capabilities';

/**
 * THE NAMES AN EXPERIENCE GIVES TO EARLIER ANSWERS.
 *
 * ## It manages NAMES, not references
 *
 * This was both at once, which is why it read as two things competing for one row. Every
 * chip-shaped job turns out to have a better home already: what a chip points at is its own
 * name and its tooltip, whether it is broken is its dashed outline and its block's issue, and
 * shortening it is a click on the chip. What is left over is all one kind of fact — where a
 * name is used, which ones are broken, what else counts as the same answer, and how to reach
 * one named on a task nobody is looking at.
 *
 * So a row is a NAME, "used in" is one of its attributes rather than a second subject, and the
 * mixture goes away.
 *
 * ## Four groups, local first
 *
 * Variables leads with the experience's own and puts the task's second, because there the
 * shared ones dominate. Here it is the other way round: the experience-wide list grows large
 * quickly and almost none of it bears on the sentence being written. So this task comes first
 * and the rest is collapsed. `attention` sits above everything, being the only group holding
 * something that has to be acted on.
 *
 * ## Broken is not a property of a name
 *
 * The same name resolves on one task and fails on another, because reachability is measured
 * from where the chip SITS. A break is therefore counted per usage and the row names the task
 * it happens on; a row that just said "broken" would be wrong on every other task using it.
 */

type RowGroup = 'attention' | 'here' | 'elsewhere' | 'unused';

export interface AliasRow {
  aliasId: string;
  alias: AnswerRefAlias | undefined;
  /** Every chip naming it, anywhere in the experience. */
  usages: AliasUsage[];
  /** The ones on the task being edited, the only ones this panel can jump to. */
  hereUsages: AliasUsage[];
  /**
   * WHETHER EVERY USE ON THIS TASK SITS AT THE EXIT, which decides whether a same-task member
   * reads as reachable — see `ReachSite`.
   *
   * On the row because two consumers need it and only one of them had it: the row rendering
   * computed it inline and the COUNT did not, so a recall used only in a summary was counted
   * broken while the row beside it read healthy. The panel then said "Needs attention" over a
   * list with nothing wrong in it, which is an error a teacher cannot act on or dismiss.
   *
   * EVERY usage rather than any: an alias quoted in a question as well as the exit IS broken in
   * the question, and the exit's healthy copy does not redeem it.
   */
  hereAtExit: boolean;
  /** Titles of the tasks where it cannot resolve. */
  breaksOn: string[];
  group: RowGroup;
}

export function aliasRows(
  tasks: Task[],
  currentTaskId: string,
  aliases: AnswerRefAlias[],
): AliasRow[] {
  const byAlias = new Map<string, AliasUsage[]>();
  for (const usage of collectAliasUsages(tasks)) {
    const list = byAlias.get(usage.aliasId);
    if (list) list.push(usage);
    else byAlias.set(usage.aliasId, [usage]);
  }

  // Every alias gets a row, used or not. An alias nothing names is precisely the one a teacher
  // could not otherwise reach, and leaving it out is what made it uneditable.
  const ids = new Set<string>([...aliases.map(a => a.id), ...byAlias.keys()]);

  const rows: AliasRow[] = [...ids].map(aliasId => {
    const alias = aliases.find(a => a.id === aliasId);
    const usages = byAlias.get(aliasId) ?? [];
    const hereUsages = usages.filter(u => u.inTaskId === currentTaskId);

    /*
     * ASKED PER USAGE, SITE AND ALL. A same-task reference is broken in a block and healthy at
     * the exit — the only difference is where it sits — so a status computed without the site
     * paints an exit's working reference red in this panel, which is what it did.
     */
    const siteOf = (u: AliasUsage) => ({ atExit: !!u.inExitSlot });
    const hereAtExit = hereUsages.length > 0 && hereUsages.every(u => !!u.inExitSlot);
    const breaksOn = [...new Set(usages
      .filter(u => !alias || aliasStatus(alias, u.inTaskId, tasks, siteOf(u)) === 'broken')
      .map(u => tasks.find(t => t.id === u.inTaskId)?.title ?? ''))].filter(Boolean);

    /*
     * ATTENTION IS SCOPED TO THIS TASK, deliberately.
     *
     * A break is per-usage, so a name can be fine here and dead three tasks away. Flagging that
     * at the top of THIS task's panel asks a teacher to fix something they cannot see, above the
     * references they came here for. It is that task's red dot, not this one's, so it sorts
     * under "used elsewhere" and still carries its red `breaksOn` line there.
     */
    const breaksHere = hereUsages.some(u =>
      !alias || aliasStatus(alias, currentTaskId, tasks, siteOf(u)) === 'broken');

    const group: RowGroup = breaksHere ? 'attention'
      : hereUsages.length > 0 ? 'here'
        : usages.length > 0 ? 'elsewhere' : 'unused';

    return { aliasId, alias, usages, hereUsages, hereAtExit, breaksOn, group };
  });

  const rank: Record<RowGroup, number> = { attention: 0, here: 1, elsewhere: 2, unused: 3 };
  return rows.sort((a, b) => rank[a.group] - rank[b.group]);
}

/**
 * What the button badge counts: chips on THIS task, and how many cannot resolve here.
 *
 * Chips rather than names, because the badge is about this task's content — a name used twice
 * here is two things to look at. And "here", because a name that breaks on another task is
 * that task's red dot, not this one's.
 */
/**
 * What is wrong with a name as a SET, in the teacher's words.
 *
 * Spelled out rather than built as `…manager.fault.${kind}` — the app has a budget of fifty
 * constructed `t()` keys, and a key written in full is one the i18n check can verify.
 */
const FAULT_TEXT: Record<AliasFault['kind'], string> = {
  notExclusive: 'blocks.answerRef.manager.faultNotExclusive',
  typeMismatch: 'blocks.answerRef.manager.faultType',
  facetMismatch: 'blocks.answerRef.manager.faultFacet',
};

/**
 * WHY AN ADDRESS CANNOT JOIN THIS NAME.
 *
 * A multi-member alias means EQUIVALENTS — one per route, of which a student answers exactly
 * one. Everything here falls out of that: a candidate a single route could answer alongside a
 * member is not an equivalent, and neither is a different kind of answer.
 *
 * REFUSED AT THE PICKER, not reported afterwards, which is the owner's call and its reason:
 * *"this would force teachers to set up flow before aliasing, but that's probably good
 * practice anyway ... set up the frame first, then do the plumbing."* The row is greyed with
 * the cause, the way the driver menu's are, so the rule teaches itself rather than arriving
 * as a rejection.
 *
 * Nothing here applies to the FIRST member. A single-member alias is the original long-name
 * use and may point anywhere at all.
 */
function memberRefusal(
  alias: AnswerRefAlias | undefined,
  ref: AnswerRef,
  aliases: AnswerRefAlias[],
  tasks: Task[],
  t: (key: string, vars?: Record<string, unknown>) => string,
): string | undefined {
  /* An address answers to one name only, or "renaming is universal" stops being true. */
  if (aliases.some(a => a.id !== alias?.id && a.members.some(m => sameRef(m, ref)))) {
    return t('blocks.answerRef.manager.ownedElsewhere');
  }
  if (!alias || alias.members.length === 0) return undefined;
  /* ALREADY IN. It is trivially non-exclusive with itself, so without this it is refused with
     "a student could answer this as well as a member" — true, and not the reason. */
  if (alias.members.some(m => sameRef(m, ref))) {
    return t('blocks.answerRef.manager.refuseAlreadyMember');
  }

  const clash = alias.members.find(
    m => !aliasMembersExclusive(m, ref, tasks as never));
  if (clash) return t('blocks.answerRef.manager.refuseNotExclusive');

  const blockOf = (r: AnswerRef) =>
    tasks.find(tk => tk.id === r.taskId)?.blocks?.find(b => b.id === r.blockId);
  const candidate = blockOf(ref);
  const existing = alias.members.map(blockOf).find(Boolean);
  /* A member whose block is gone says nothing here — that is the alias's broken state to
     report, and two sentences about one problem is how a list stops being read. */
  if (candidate && existing && candidate.type !== existing.type) {
    return t('blocks.answerRef.manager.refuseType');
  }
  if (alias.members.some(m => m.facet !== ref.facet)) {
    return t('blocks.answerRef.manager.refuseFacet');
  }
  return undefined;
}

export function answerRefCount(
  tasks: Task[],
  currentTaskId: string,
  aliases: AnswerRefAlias[],
): { total: number; broken: number } {
  let total = 0;
  let broken = 0;
  for (const row of aliasRows(tasks, currentTaskId, aliases)) {
    total += row.hereUsages.length;
    /* A MALFORMED alias counts too, and counts everywhere it is used: unlike reach, which is
       a question about this task, several members a single route can answer is wrong wherever
       the name appears. The chip here cannot be trusted, so the dot here lights. */
    /* `row.hereAtExit`, not a bare call: the row this count describes resolves the same alias
       WITH the site, and the two disagreeing is what put an unexplainable error on the panel. */
    const brokenHere = !row.alias
      || aliasStatus(row.alias, currentTaskId, tasks, { atExit: row.hereAtExit }) === 'broken'
      || aliasFormationFaults(row.alias, tasks as never).length > 0;
    if (brokenHere) broken += row.hereUsages.length;
  }
  return { total, broken };
}

export function AnswerRefManager({ tasks, currentTaskId, onOpenBlock, onOpenTask, onRepoint }: {
  tasks: Task[];
  currentTaskId: string;
  /** Open the block a chip sits in — only ever one on this task. */
  onOpenBlock: (blockId: string) => void;
  /** Travel to a task a name is used on. Absent where the surface cannot navigate. */
  onOpenTask?: (taskId: string) => void;
  /**
   * Point a name at a different answer. Every chip naming it follows.
   *
   * `replacing` names WHICH address is moving, for a name holding several. Without it the
   * others were silently dropped, and nothing on screen said which one had been retargeted.
   */
  onRepoint: (aliasId: string, to: AnswerRef, replacing?: AnswerRef) => void;
}) {
  const { t } = useTranslation();
  const identity = useBlockIdentity();
  const scope = useAnswerRefScope();
  /* The rung's source half, so a member cannot be ADDED that the rung would not let anyone
     recall — the add picker and the insert pickers offer the same set. */
  const { level } = useCapabilities();
  const sourceRung = level('answerRecall');
  const aliases = useMemo(() => scope?.aliases ?? [], [scope]);
  /* Tasks and names together, because a derived name reads both — see `NameSource`. */
  const nameSrc = useMemo(() => ({ tasks, aliases }), [tasks, aliases]);
  const rows = useMemo(
    () => aliasRows(tasks, currentTaskId, aliases), [tasks, currentTaskId, aliases]);

  const [openRow, setOpenRow] = useState<string | null>(null);
  /** The unused row whose trash is armed — see the control's own note. */
  const [armedRow, setArmedRow] = useState<string | null>(null);
  const removeAlias = scope?.removeAlias;
  /** Which address is one press from being removed. Keyed by alias AND address: the same
   *  answer can belong to two names, and arming one must not arm the other. */
  const [armedMember, setArmedMember] = useState<string | null>(null);
  /*
   * NO SEPARATE "disarm when the row closes".
   *
   * The ring is painted on the address lines, which stay visible when the row shuts, so it
   * looked like one was needed — and a synthetic click in a probe made it look broken. A real
   * click focuses the trash, so collapsing the row blurs it and `onBlur` below disarms. The
   * effect could never fire for anyone using a mouse or a keyboard, and no test could hold it
   * down.
   */
  // The two distant groups start shut. They are the long ones and neither bears on the
  // sentence being written: a teacher opens them to go looking, not to be shown.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(
    { attention: true, here: true, elsewhere: false, unused: false });

  const addMember = (aliasId: string, to: AnswerRef) => {
    const alias = aliases.find(a => a.id === aliasId);
    if (!alias || alias.members.some(m => sameRef(m, to))) return;
    const task = tasks.find(tk => tk.id === to.taskId);
    const block = task?.blocks?.find(b => b.id === to.blockId);
    const label = [task?.title, block?.name].filter(Boolean).join(' · ');
    scope?.updateAlias(aliasId, { members: [...alias.members, { ...to, label }] });
  };

  const removeMember = (aliasId: string, member: AnswerRef) => {
    const alias = aliases.find(a => a.id === aliasId);
    if (!alias) return;
    const next = membersWithout(alias, member);
    if (next) scope?.updateAlias(aliasId, { members: next });
  };

  /** A member, described from its target while that exists and from its epitaph once it does not. */
  const memberLine = (m: AnswerRef & { label?: string }) => {
    const task = tasks.find(tk => tk.id === m.taskId);
    const block = task?.blocks?.find(b => b.id === m.blockId);
    // The shared spelling, which the chip's own menu now shows too — one address written two
    // ways is two answers to "where did this come from".
    return task && block
      ? answerRefAddress(nameSrc, m, t)
      : t('blocks.answerRef.manager.wasPointingAt', {
        path: m.label || t('blocks.answerRef.missingSource'),
      });
  };
  const memberLives = (m: AnswerRef) =>
    !!tasks.find(tk => tk.id === m.taskId)?.blocks?.some(b => b.id === m.blockId);
  /* What clearing the field returns the name to — the block's own spelling, never the
     family's, or the placeholder would echo the name it is offering to remove. */
  const derivedName = (alias: AnswerRefAlias | undefined) =>
    aliasDisplayName(alias, nameSrc, t, { omitTask: true, ignoreFamily: true });
  const facetSuffix = (alias: AnswerRefAlias | undefined) => {
    const first = alias?.members[0];
    return first ? answerRefFacetSuffix(nameSrc, first, t) : '';
  };

  /**
   * WHY this address cannot be used, in the order the causes rule each other out.
   *
   * There is more than one way to be broken and they want different repairs — the answer was
   * deleted, or it is still there but can no longer be recalled (optional, media, an order),
   * or it is fine and simply never reached before here. A single red line saying "not
   * available" sends a teacher looking for the wrong thing.
   *
   * Returns null when the address is usable, so a caller can map over members and keep only
   * what it finds.
   */
  const memberProblem = (m: AnswerRef, atExit = false): string | null => {
    const block = tasks.find(tk => tk.id === m.taskId)?.blocks?.find(b => b.id === m.blockId);
    if (!block) return t('blocks.answerRef.manager.whyGone');
    const eligibility = answerRefEligibility(block.type, (block.data ?? {}) as Record<string, unknown>);
    // The block's own reason, which already reads as a short phrase — the picker shows the
    // same words for the same state, so a teacher meets one explanation, not two.
    if (!eligibility.referenceable) return t(`blocks.answerRef.blocked.${eligibility.reason}`);
    // `atExit` because the same address is unreachable from a block and perfectly reachable
    // from the task's exit — see `ReachSite`. Without it this line called an exit's working
    // reference "answered after this task", which is the opposite of true.
    if (answerRefStatus(m, currentTaskId, tasks as never, { atExit }) === 'broken') {
      return t('blocks.answerRef.manager.whyUnreached');
    }
    return null;
  };

  if (rows.length === 0) {
    return (
      <div className='px-4 py-6 text-sm text-ws-muted'>
        {t('blocks.answerRef.manager.empty')}
      </div>
    );
  }

  const GROUPS: { key: RowGroup; label: string; tone: string }[] = [
    { key: 'attention', label: t('blocks.answerRef.manager.groupAttention'), tone: 'text-ws-error-fg' },
    { key: 'here', label: t('blocks.answerRef.manager.groupHere'), tone: 'text-ws-muted' },
    { key: 'elsewhere', label: t('blocks.answerRef.manager.groupElsewhere'), tone: 'text-ws-muted' },
    { key: 'unused', label: t('blocks.answerRef.manager.groupUnused'), tone: 'text-ws-muted' },
  ];

  return (
    /* THE BODY SCROLLS, never the shell around it — the header holding the close button is up
       there, and it used to scroll away with the list. `min-h-0` is what lets this shrink
       inside its parent instead of pushing the shell taller than the panel. */
    <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3'>
      {GROUPS.map(group => {
        const members = rows.filter(r => r.group === group.key);
        if (members.length === 0) return null;
        const open = openGroups[group.key] ?? false;

        return (
          <section key={group.key}>
            <div className='mb-1.5 flex items-center gap-1'>
              <IconButton
                size='sm'
                aria-expanded={open}
                aria-label={t(open
                  ? 'blocks.answerRef.manager.collapseGroup'
                  : 'blocks.answerRef.manager.expandGroup', { name: group.label })}
                onClick={() => setOpenGroups(prev => ({ ...prev, [group.key]: !open }))}
              >
                {open ? <ChevronDown aria-hidden='true' className='h-3.5 w-3.5' />
                  : <ChevronRight aria-hidden='true' className='h-3.5 w-3.5' />}
              </IconButton>
              <p className={`text-xs font-semibold ${group.tone}`}>{group.label}</p>
              <span className='text-xs text-ws-muted'>{members.length}</span>
            </div>

            {open && (
              <ul className='flex flex-col gap-1.5'>
                {members.map(row => {
                  const name = aliasDisplayName(row.alias, nameSrc, t);
                  const rowOpen = openRow === row.aliasId;
                  const broken = row.group === 'attention';

                  // The OTHER tasks this name is used on, in experience order so the list
                  // reads the way the board does.
                  /*
                   * THE HEALTH OF THE REFERENCE, which is a different question from whether
                   * its address resolves.
                   *
                   * Soil/DD pointed at a live dropdown in a live task — every address fine —
                   * and was still unusable here, because that task sits in a pathway this one
                   * is not reached through. Nothing on the closed card said so: the addresses
                   * read clean, and the only signal was a faint border and which group it had
                   * been filed under.
                   *
                   * Only for a name used on THIS task. Asked about one used elsewhere the
                   * question is meaningless, and those rows already carry it on their task
                   * chips.
                   */
                  // Derived once, on the row — see `AliasRow.hereAtExit` for why it moved.
                  const hereAtExit = row.hereAtExit;
                  const hereStatus = row.hereUsages.length > 0
                    // A chip naming no alias at all — vanished, or authored before aliases —
                    // can never resolve, and is the case with the least to go on otherwise.
                    ? (!row.alias ? 'broken' : aliasStatus(row.alias, currentTaskId, tasks, { atExit: hereAtExit }))
                    /*
                     * AND A NAME USED ONLY ELSEWHERE STILL REPORTS ITS TROUBLE.
                     *
                     * Gated on `hereUsages` this said nothing at all about the rows under
                     * "used elsewhere" — which is where a teacher meets a name whose problem
                     * is on a task they are not looking at, and the one case they cannot see
                     * for themselves. Reported exactly that way: a broken name, collapsed,
                     * with no mark on it.
                     */
                    : row.breaksOn.length > 0 ? 'broken' : 'ok';
                  /*
                   * MALFORMED OUTRANKS REACH, and is the one state that is not about this
                   * task. Reach asks "can a student here have answered it"; a name holding
                   * several answers one route can give is wrong wherever it appears, so it
                   * shows the error mark on every task that uses it and says the same thing
                   * on each. Checked first for that reason: a malformed alias whose coverage
                   * happens to be total here would otherwise read as healthy.
                   */
                  const faults = row.alias ? aliasFormationFaults(row.alias, tasks as never) : [];
                  const StatusIcon = faults.length > 0 || hereStatus === 'broken' ? OctagonAlert
                    : hereStatus === 'conditional' ? AlertTriangle
                      : null;
                  /* Which routes arrive with nothing — the "finish this" half of a partial
                     cover. Named by task, because that is where the teacher has to go. */
                  const uncovered = row.alias && hereStatus === 'conditional'
                    ? aliasUncoveredFrom(row.alias, currentTaskId, tasks as never)
                      .map(id => tasks.find(tk => tk.id === id))
                      .map(tk => (tk as { pathwayName?: string } | undefined)?.pathwayName
                        || tk?.title || '')
                      .filter(Boolean)
                    : [];

                  const kind = row.alias ? aliasKind(row.alias, tasks as never) : null;
                  /*
                   * AND WHICH BLOCK TYPE IT QUOTES, which the kind letter cannot say.
                   *
                   * `#` and `A` answer "may this enter an equation"; they do not say whether the
                   * number came from a Numerical or a Slider, or the word from a Keyword or a
                   * Dropdown. The link menu grew the library glyph for exactly that and the owner
                   * asked for it here — "it reminds me which block type it's tied to". In FRONT
                   * of the kind letter, which is the order the recall picker already uses.
                   *
                   * ONE GLYPH for the whole alias: `typeMismatch` refuses a mixed one at
                   * formation, so every member is the same type by construction — and a row that
                   * slipped through anyway shows `mixed` on the kind letter beside it.
                   */
                  const TypeIcon = (() => {
                    const first = row.alias?.members
                      .map(m => tasks.find(tk => tk.id === m.taskId)?.blocks?.find(b => b.id === m.blockId))
                      .find(Boolean);
                    return first ? BLOCK_META[first.type]?.Icon : undefined;
                  })();
                  const usedOn = new Set(row.usages.map(u => u.inTaskId));
                  const otherTasks = tasks.filter(tk => tk.id !== currentTaskId && usedOn.has(tk.id));

                  return (
                    <li
                      key={row.aliasId || 'unaliased'}
                      className={`rounded-xl border bg-ws-surface px-3 py-2.5 ${broken
                        ? 'border-ws-error-fg/40'
                        : rowOpen ? 'border-ws-primary' : 'border-ws-border'}`}
                    >
                      <div className='flex items-center gap-2'>
                        {/*
                          * WHAT THIS NAME QUOTES: a number or a word.
                          *
                          * It decides whether the name may enter an equation, and until now it
                          * was invisible — the paste check and the re-anchor filter both enforce
                          * it, so a teacher met it only as a refusal. In FRONT of the field,
                          * because it qualifies the name rather than commenting on it.
                          *
                          * `mixed` means the name spans different question types, which is
                          * almost always a mistake — the structure that legitimately joins
                          * several addresses joins them at ONE kind. Warned, not forbidden:
                          * house rule is to warn when in doubt. It takes the app's own warning
                          * glyph, unlike the chip, because a panel row is where that vocabulary
                          * belongs.
                          */}
                        {TypeIcon && (
                          <TypeIcon aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-muted' />
                        )}
                        {kind && (
                          <span
                            className={`shrink-0 font-mono text-[10px] leading-none ${
                              kind === 'mixed' ? 'text-ws-warning-fg' : 'text-ws-muted'}`}
                            title={t(`blocks.answerRef.manager.kind.${kind}`)}
                          >
                            {kind === 'mixed'
                              ? <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5' />
                              : <span aria-hidden='true'>{kind === 'number' ? '#' : 'A'}</span>}
                            <span className='sr-only'>{t(`blocks.answerRef.manager.kind.${kind}`)}</span>
                          </span>
                        )}
                        {/* The field REPLACES the name rather than sitting under it: an open row
                            saying its own name twice is the bulk the panel was shrunk to lose. */}
                        {rowOpen && row.alias ? (
                          <div className='flex min-w-0 flex-1 items-center gap-1.5'>
                          <CommitField
                            value={row.alias.name ?? ''}
                            placeholder={derivedName(row.alias)}
                            ariaLabel={t('blocks.answerRef.manager.nameLabel', { name })}
                            onCommit={next => scope?.updateAlias(row.aliasId, {
                              name: next.trim() === '' || next.trim() === derivedName(row.alias)
                                ? null : next.trim(),
                            })}
                          />
                          {/*
                            * WHICH PART OF THE ANSWER, beside the field and not in it.
                            *
                            * The field holds the FAMILY's name, which every facet of a block
                            * shares — so open two of them and both say `kv`, with nothing to
                            * tell them apart. Reported as "i renamed it to kv, kv (the number
                            * version) kept its name without any indicator what it is".
                            *
                            * Beside rather than inside, because the suffix is not part of the
                            * name: "(full), (number), (uncertainty), and so on are
                            * automatically appended to the chip, but never on the alias".
                            * Aria-hidden because the field's own label already carries the
                            * whole spelling.
                            */}
                          {facetSuffix(row.alias) && (
                            <span aria-hidden='true' className='shrink-0 text-xs text-ws-muted'>
                              {facetSuffix(row.alias)}
                            </span>
                          )}
                          </div>
                        ) : (
                          <span className='min-w-0 flex-1 truncate text-sm font-medium text-ws-text'>{name}</span>
                        )}
                        {StatusIcon && !rowOpen && (
                          <span className={`flex shrink-0 items-center ${faults.length > 0 || hereStatus === 'broken' ? 'text-ws-error-fg' : 'text-ws-warning-fg'}`}>
                            <StatusIcon aria-hidden='true' className='h-3.5 w-3.5' />
                            <span className='sr-only'>
                              {faults[0]
                                ? t(FAULT_TEXT[faults[0].kind])
                                : hereStatus === 'conditional'
                                ? t('blocks.answerRef.manager.statusConditionalHere')
                                : row.hereUsages.length > 0
                                  ? t('blocks.answerRef.manager.statusBrokenHere')
                                  // Named, because the whole point of the row is that the
                                  // trouble is somewhere the teacher cannot see from here.
                                  : t('blocks.answerRef.manager.statusBrokenOn', { tasks: row.breaksOn.join(', ') })}
                            </span>
                          </span>
                        )}
                        {!broken && row.usages.length > 0 && (
                          <span className='shrink-0 text-xs text-ws-muted'>
                            {/* "N uses", not "N blocks". Summary feedback is not a block and
                                never was — the owner's words — and this count has always been
                                over USAGES, which a block count only happened to describe
                                while a chip could live nowhere else. */}
                            {t('blocks.answerRef.manager.useCount', { count: row.usages.length })}
                          </span>
                        )}
                        {/*
                          * REMOVING A NAME NOTHING CITES, on the one group where that is true.
                          *
                          * The sweep clears these on its own when a chip or a block goes, but
                          * only from the moment it is armed — a name stranded by an earlier
                          * session has no route out. Reported as "i don't see a way to clean
                          * this up ... but i'm also not sure if cleanup is desirable, as you
                          * might want to use it later", which is why it is a control here
                          * rather than a wider sweep: the teacher decides, one name at a time.
                          *
                          * ARMED, like the member trash below it and for the same reason, and
                          * UNDOABLE — one press of undo brings the name back.
                          */}
                        {group.key === 'unused' && removeAlias && (
                          <IconButton
                            type='button'
                            variant='destructive'
                            size='sm'
                            isArmed={armedRow === row.aliasId}
                            className='shrink-0'
                            aria-label={t(armedRow === row.aliasId
                              ? 'blocks.answerRef.manager.confirmRemoveAlias'
                              : 'blocks.answerRef.manager.removeAlias', { name })}
                            onClick={() => {
                              if (armedRow === row.aliasId) { removeAlias(row.aliasId); setArmedRow(null); }
                              else setArmedRow(row.aliasId);
                            }}
                            onPointerLeave={e => {
                              if (e.pointerType !== 'touch' && armedRow === row.aliasId) setArmedRow(null);
                            }}
                            onBlur={() => { if (armedRow === row.aliasId) setArmedRow(null); }}
                            onKeyDown={e => {
                              if (e.key === 'Escape' && armedRow === row.aliasId) {
                                e.stopPropagation(); setArmedRow(null);
                              }
                            }}
                          >
                            <Trash2 aria-hidden='true' className='h-3 w-3' />
                          </IconButton>
                        )}
                        <IconButton
                          size='sm'
                          aria-expanded={rowOpen}
                          aria-label={t(rowOpen
                            ? 'blocks.answerRef.manager.collapseRow'
                            : 'blocks.answerRef.manager.expandRow', { name })}
                          onClick={() => setOpenRow(rowOpen ? null : row.aliasId)}
                        >
                          {rowOpen ? <ChevronDown aria-hidden='true' className='h-3.5 w-3.5' />
                            : <ChevronRight aria-hidden='true' className='h-3.5 w-3.5' />}
                        </IconButton>
                      </div>

                      {/* WHAT IT RESOLVES TO, closed as well as open. A name on its own could
                          mean any block called that, which is what the short form got wrong.
                          No gap between lines: each elbow's leg rises to the bottom of the one
                          above it, so the legs read as one trunk descending from the name. */}
                      <ul className={`flex flex-col ${rowOpen ? 'mt-1.5' : 'mt-0.5'}`}>
                        {(row.alias?.members ?? []).map(m => {
                          const key = `${row.aliasId}|${m.taskId}|${m.blockId}|${m.facet}`;
                          const armed = armedMember === key;
                          /*
                           * A DEAD ADDRESS IS MARKED, not merely tinted — the same reason the
                           * chips are, and this is where a teacher looks to find out WHICH
                           * answer went.
                           *
                           * Warning or error depending on what it costs. One address of
                           * several, with a living sibling, leaves the reference working: the
                           * teacher has lost an equivalent, which is a warning. The last one
                           * dying takes the reference with it, which is an error. That is the
                           * app's own split between the triangle and the octagon.
                           */
                          const dead = !memberLives(m);
                          const stillResolves = (row.alias?.members ?? []).some(memberLives);

                          /*
                           * THE MARK GOES ON THE ADDRESS THAT CAUSES IT, once the row is open.
                           *
                           * Closed, one icon beside the name is the summary. Open, a name can
                           * hold several addresses and only some of them are the problem —
                           * owner: "there could be multiple blocks in the alias". So each line
                           * carries its own verdict and the header's goes quiet.
                           *
                           * `answerRefStatus` rather than `memberLives`: an address can be
                           * perfectly real and still unreachable from here, which is the case
                           * a teacher is least able to work out for themselves.
                           *
                           * ASKED FROM WHERE THE NAME IS USED, not from the task being looked
                           * at. "Unreachable from here" is only a fault if a chip is standing
                           * here needing it — and this asked it of every row on every task, so
                           * opening the panel on a task that uses none of them painted all of
                           * them red. Reported exactly that way: go back a task and every
                           * recall is flagged, though none of them is used there.
                           *
                           * Worst case across the tasks that DO use it, which is the same rule
                           * `breaksOn` already follows one field up: a reference broken on one
                           * task it is used on is broken, whatever it does on the others. A
                           * name nothing uses is asked nothing, because there is no chip whose
                           * resolution could fail.
                           */
                          const askFrom = row.hereUsages.length > 0
                            ? [currentTaskId]
                            : [...new Set(row.usages.map(u => u.inTaskId))];
                          /*
                           * WITH THE SITE, which this line alone was still asking without.
                           *
                           * A same-task address is `never` from a block and `always` from the
                           * exit — the row's own status learned that long ago (`siteOf`), and
                           * the per-member line did not. So "You said {MCQ 1}" written into
                           * Animal Test's own All Correct exit, which is the one place that
                           * reference is legal, carried a red octagon. Reported as "the recall
                           * needs to lose its error, it's being used legally".
                           *
                           * A task holding BOTH an exit chip and a block chip reads `false`:
                           * the block is the stricter site and the one that can actually break.
                           */
                          const atExitOn = new Map<string, boolean>();
                          for (const u of row.usages) {
                            atExitOn.set(u.inTaskId, (atExitOn.get(u.inTaskId) ?? true) && !!u.inExitSlot);
                          }
                          const RANK = { ok: 0, conditional: 1, broken: 2 } as const;
                          const memberStatus = askFrom.reduce<'ok' | 'conditional' | 'broken'>(
                            (worst, from) => {
                              const st = answerRefStatus(m, from, tasks as never,
                                { atExit: atExitOn.get(from) ?? false });
                              return RANK[st] > RANK[worst] ? st : worst;
                            }, 'ok');
                          /*
                           * A MEMBER BEING CONDITIONAL IS WHAT AN ALIAS IS FOR, so it is only
                           * worth saying when the alias does not cover everything.
                           *
                           * Three arrivals on three pathways: each address alone is reachable
                           * only sometimes, and together they are reachable always. Marking
                           * each of the three was the panel reporting the mechanism as a fault
                           * — "there should be no warnings because all paths are covered". A
                           * DEAD address still speaks for itself, because that is a fault
                           * whatever the others do.
                           */
                          const memberFault = memberStatus === 'conditional' && hereStatus === 'ok'
                            ? 'ok' : memberStatus;
                          const MemberIcon = dead
                            ? (stillResolves ? AlertTriangle : OctagonAlert)
                            : memberFault === 'broken' ? OctagonAlert
                              : memberFault === 'conditional' ? AlertTriangle : null;
                          const memberTone = dead
                            ? (stillResolves ? 'text-ws-warning-fg' : 'text-ws-error-fg')
                            : memberFault === 'broken' ? 'text-ws-error-fg'
                              : memberFault === 'conditional' ? 'text-ws-warning-fg' : '';
                          const DeadIcon = MemberIcon;
                          return (
                            <li
                              key={`${m.taskId}-${m.blockId}-${m.facet}`}
                              /* WRAPS, because the two messages below are full-width children
                                 of this same row. Without it they were flex items competing with
                                 the address for one line, and the address is the one that gives:
                                 it carries `min-w-0` so a long task title can shrink, and
                                 `break-words` then broke it to ONE CHARACTER PER LINE while
                                 "Re-anchor" hung off the panel's right edge. Reported from a
                                 screenshot. They always wanted their own line — both were
                                 written with `mt-1`, which only means anything in a column. */
                              className={`flex flex-wrap items-center gap-1.5 text-xs ${memberTone || 'text-ws-muted'} ${armed
                                // HORIZONTAL BLEED ONLY, not the shared `ARMED_DELETE_BLEED`.
                                // These lines sit flush so the trunk of elbows reads as one
                                // line, which leaves no vertical room: the standard -my-1
                                // drew the ring across the address above and below it.
                                ? `rounded ${ARMED_DELETE_PAINT} -mx-1 px-1`
                                : ''}`}
                            >
                              {/* The elbow tying this answer to the NAME above it. Drawn in
                                  borders rather than set as "\u2514", because a box-drawing
                                  character in the accessibility tree is read out as a word. */}
                              <span
                                aria-hidden='true'
                                className='ml-1 h-[0.6rem] w-2 shrink-0 self-start rounded-bl-[3px] border-b border-l border-ws-muted/40'
                              />
                              {DeadIcon && (
                                <DeadIcon aria-hidden='true' className='h-3 w-3 shrink-0' />
                              )}
                              {/*
                                * WRAPS, like the same address does in the chip's own menu.
                                *
                                * It truncated, and an address is a task title plus a block name
                                * — both of them sentences the teacher wrote. Two long ones and
                                * the line was an ellipsis before it reached the part that says
                                * WHICH answer, which is the only reason the line is here.
                                * Reported that way: impossible to see what the alias is keyed
                                * to. The panel is a column with room to grow downward, so the
                                * second line costs nothing the first was using.
                                */}
                              <span className='min-w-0 flex-1 break-words'>
                                {memberLine(m)}
                                {dead && (
                                  <span className='sr-only'>{` ${t(stillResolves
                                    ? 'blocks.answerRef.manager.memberGoneWarning'
                                    : 'blocks.answerRef.manager.memberGoneError')}`}</span>
                                )}
                              </span>

                              {/* RE-ANCHOR SITS ON THE ADDRESS IT MOVES.
                                  The code still says "repoint" throughout, including
                                  `repointAnswerRefs` in the blocks package. That is the
                                  mechanism; "re-anchor" is what a teacher calls it, and the
                                  two are allowed to differ as long as only one of them is
                                  ever on screen. As one control per name
                                  it silently collapsed a name holding two equivalent answers to
                                  whichever one was chosen, with nothing on screen saying which
                                  was being retargeted. */}
                              {/*
                                * WHAT IS WRONG, AND WHAT WOULD FINISH IT — visible only on an
                                * OPEN row, where the teacher is already looking at the members
                                * and can act on it. Collapsed, the mark on the row is the
                                * whole message; a sentence on every collapsed row would be the
                                * panel's own noise.
                                *
                                * A fault and a gap never show together: a malformed name has
                                * no meaningful coverage to be short of, and saying both would
                                * be two instructions for one repair.
                                */}
                              {rowOpen && faults[0] && (
                                /* `order-last basis-full`: its own line, drawn under the address
                                   and its controls, while the DOM keeps it beside the address it
                                   is about — which is the order a screen reader wants. */
                                <p className='order-last mt-1 basis-full text-xs text-ws-error-fg'>
                                  {t(FAULT_TEXT[faults[0].kind])}
                                </p>
                              )}
                              {rowOpen && (
                                <AnswerRefPickerMenu
                                  /*
                                   * A NAME USED IN A FORMULA MAY ONLY MOVE TO A NUMBER.
                                   *
                                   * The insert menu already refuses a word in an equation, so
                                   * the equation is correct the moment it is written — and
                                   * then re-anchoring could point it at a keyword, leaving the
                                   * formula untouched while what it quotes stops being a
                                   * number. The equation keeps working and yields nothing.
                                   *
                                   * `inFormula` is how a usage says it is arithmetic: a
                                   * formula field stores `[ara_…]`, prose stores a span, and
                                   * the scan already told them apart. No table of field paths
                                   * per block type, which would be wrong the first time a
                                   * block grew a field.
                                   */
                                  kinds={row.usages.some(u => u.inFormula) ? 'number' : 'any'}
                                  tasks={tasks}
                                  currentTaskId={currentTaskId}
                                  onChoose={to => onRepoint(row.aliasId, to, m)}
                                  ariaLabel={t('blocks.answerRef.manager.reanchorLabel', { name: memberLine(m) })}
                                  renderTrigger={trigger => (
                                    <button
                                      {...trigger}
                                      type='button'
                                      aria-label={t('blocks.answerRef.manager.reanchorLabel', { name: memberLine(m) })}
                                      className='shrink-0 text-xs text-ws-text-link hover:underline'
                                    >
                                      {t('blocks.answerRef.manager.reanchor')}
                                    </button>
                                  )}
                                />
                              )}

                              {/* ARMED TRASH, not a bare X. Removing an address is an edit that
                                  cannot be read back off the screen afterwards, so it follows
                                  the same two-press rule as every other destructive control. An
                                  X stays what it is elsewhere: dismissing something. */}
                              {rowOpen && (row.alias?.members.length ?? 0) > 1 && (
                                <IconButton
                                  type='button'
                                  variant='destructive'
                                  size='xs'
                                  isArmed={armed}
                                  className='shrink-0'
                                  aria-label={t(armed
                                    ? 'blocks.answerRef.manager.confirmRemoveMember'
                                    : 'blocks.answerRef.manager.removeMember', { name: memberLine(m) })}
                                  onClick={() => {
                                    if (armed) { removeMember(row.aliasId, m); setArmedMember(null); }
                                    else setArmedMember(key);
                                  }}
                                  onPointerLeave={e => { if (e.pointerType !== 'touch' && armed) setArmedMember(null); }}
                                  onBlur={() => { if (armed) setArmedMember(null); }}
                                  onKeyDown={e => { if (e.key === 'Escape' && armed) { e.stopPropagation(); setArmedMember(null); } }}
                                >
                                  <Trash2 aria-hidden='true' className='h-3 w-3' />
                                </IconButton>
                              )}
                            </li>
                          );
                        })}
                      </ul>

                      {/* UNDER THE LAST ADDRESS, because that is what it adds one to. In a row
                          of actions at the foot of the card it read as another thing the NAME
                          does, alongside controls that act on something else entirely. */}
                      {rowOpen && row.alias && (
                        <div className='ml-3.5 mt-0.5'>
                          {/*
                            * THE CONTROL IS THERE WHEN AN EQUIVALENCE IS POSSIBLE, AND
                            * OTHERWISE IT IS NOT THERE. Nothing is said in its place.
                            *
                            * It used to open a menu in which every row was greyed with its
                            * reason — "i'm seeing that nothing can be linked up, everything is
                            * greyed out" — and then, briefly, to print those reasons as a
                            * sentence instead, which is a note explaining a control that is
                            * not on screen. The owner: "no need to have a note about why
                            * equivalencies aren't allowed ... the button is there when it's
                            * possible not when it isn't, with a warning if there is an
                            * existing equivalence that is disqualified because the blocks were
                            * moved onto the same path".
                            *
                            * That warning is a DIFFERENT thing in a different place: a name
                            * whose members have stopped being exclusive is a mistake to fix,
                            * and `aliasFormationFaults` reports it on the row itself. A menu
                            * with nothing in it is not a mistake, it is an experience that
                            * has nowhere to put a second address.
                            *
                            * Greying still teaches the rule where SOME candidate qualifies:
                            * the row a teacher reaches for explains itself, and the menu they
                            * opened was worth opening.
                            *
                            * The picker's OWN sections, not a second enumeration: two answers
                            * to "what could join this name" is how a button comes to offer a
                            * menu that then refuses everything in it.
                            */}
                          {(() => {
                            /*
                             * A MEMBER IS NOT A FAILED CANDIDATE. The addresses this name
                             * already holds come back refused with "already part of this
                             * name", which is true and is not a reason equivalence is
                             * impossible — left in, a two-task experience whose only other
                             * answer IS the member withheld the control with no candidate
                             * having failed. Dropped by ADDRESS, not by matching a sentence.
                             */
                            /* The same exclusion the menu below makes, or this count decides
                               whether to show a picker that will list something else. The
                               panel sits at task level and so has no block identity of its
                               own — both read `undefined` here, which is the point: they read
                               the SAME thing. */
                            const candidates = answerRefSections(
                              tasks, currentTaskId, t, aliases,
                              ref => memberRefusal(row.alias, ref, aliases, tasks, t), 'any',
                              { selfBlockId: identity?.blockId }, sourceRung,
                            ).flatMap(sec => sec.options)
                              .filter(o => !(o.ref
                                && (row.alias?.members ?? []).some(m => sameRef(m, o.ref!))));
                            if (candidates.length === 0) return null;
                            if (candidates.every(o => o.disabled)) return null;
                            return (
                          <AnswerRefPickerMenu
                            tasks={tasks}
                            currentTaskId={currentTaskId}
                            unavailable={ref => memberRefusal(row.alias, ref, aliases, tasks, t)}
                            onChoose={to => addMember(row.aliasId, to)}
                            ariaLabel={t('blocks.answerRef.manager.addMemberLabel', { name })}
                            renderTrigger={trigger => (
                              <button
                                {...trigger}
                                type='button'
                                aria-label={t('blocks.answerRef.manager.addMemberLabel', { name })}
                                className='flex items-center gap-1 text-xs text-ws-text-link hover:underline'
                              >
                                <Plus aria-hidden='true' className='h-3 w-3' />
                                {t('blocks.answerRef.manager.addMember')}
                              </button>
                            )}
                          />
                            );
                          })()}

                          {/*
                            * THE WARNING, THEN THE FIELD IT EXISTS FOR.
                            *
                            * Only where it has a case: a name reached on every route cannot
                            * come up empty, so the field there is a control that can never
                            * fire. On a conditional one it can, and the field is what the
                            * student reads instead — so the warning says what is going to
                            * happen and the field answers it, in that order.
                            */}
                          {/*
                            * THE ERROR SAYS WHICH KIND OF BROKEN, in the same place the
                            * warning sits. There is more than one way — the answer was
                            * deleted, it can no longer be recalled, it is never reached
                            * before here — and they want different repairs. Listed distinct,
                            * because two addresses dead the same way is one thing to fix.
                            */}
                          {rowOpen && hereStatus === 'broken' && (() => {
                            const why = [...new Set((row.alias?.members ?? [])
                              .map(m => memberProblem(m, hereAtExit)).filter((r): r is string => !!r))];
                            // A chip naming no alias has no address to diagnose.
                            const reasons = why.length > 0 ? why : [t('blocks.answerRef.manager.whyNoAlias')];
                            return (
                              <p className='mt-1.5 flex items-start gap-1 text-xs text-ws-error-fg'>
                                <OctagonAlert aria-hidden='true' className='mt-0.5 h-3 w-3 shrink-0' />
                                <span>{reasons.join(' \u00b7 ')}</span>
                              </p>
                            );
                          })()}

                          {/*
                            * NOT WHILE THE NAME IS MALFORMED. A fault and a gap never show
                            * together — a name a single student can answer twice has no
                            * meaningful coverage to be short of, and two instructions for one
                            * repair is how a panel stops being read.
                            *
                            * The old per-member warning carried this guard; moving the sentence
                            * here dropped it, and the test that says so caught it.
                            */}
                          {rowOpen && row.alias && hereStatus === 'conditional' && !faults[0] && (
                            <div className='mt-1.5 flex flex-col gap-1'>
                              {/*
                                * ONE SENTENCE, HERE, naming the routes.
                                *
                                * It used to be two things in two places: "May not be answered by
                                * everyone" above this field, and "Students on X arrive with no
                                * answer for this" repeated once PER MEMBER in the list above —
                                * so a three-member alias said the same thing three times and
                                * then said it differently a fourth. The row-level fact was
                                * already computed once; only the rendering was per member.
                                *
                                * Naming the routes is the half worth keeping: "may not be
                                * answered by everyone" tells a teacher to worry, and "students
                                * on Mountain Pathway arrive with no answer" tells them where to
                                * go. And it belongs HERE because the field below is the answer
                                * to it — the warning says what will happen, the field says what
                                * the student reads instead.
                                */}
                              <p className='flex items-start gap-1 text-xs text-ws-warning-fg'>
                                <AlertTriangle aria-hidden='true' className='mt-0.5 h-3 w-3 shrink-0' />
                                <span>
                                  {t('blocks.answerRef.manager.uncoveredRoutes',
                                    { count: uncovered.length, routes: uncovered.join(', ') })}
                                </span>
                              </p>
                              <CommitField
                                value={row.alias.fallback ?? ''}
                                placeholder={t('blocks.answerRef.manager.fallbackPlaceholder')}
                                ariaLabel={t('blocks.answerRef.manager.fallbackLabel', { name })}
                                onCommit={next => scope?.updateAlias(row.aliasId, { fallback: next || null })}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {rowOpen && (
                        <div className='mt-2 flex flex-col gap-2 border-t border-ws-border pt-2'>
                          {/*
                            * WHERE IT IS USED: blocks here, then the other tasks.
                            *
                            * These answer different questions with different reach. A block
                            * chip opens a block on THIS task, which this page can do. A task
                            * chip travels. Keeping them in one undifferentiated row made the
                            * second look like more of the first.
                            *
                            * The broken one is TINTED rather than described in a sentence. The
                            * sentence said "breaks on New Task" — a name, no way to get there,
                            * and nothing to press. The thing to press is the thing to mark.
                            */}
                          {row.hereUsages.length > 0 && (
                            <ul className='flex flex-wrap gap-1.5'>
                              {[...new Map(row.hereUsages.map(u => [u.inExitSlot ?? u.inBlockId, u])).values()].map(usage => {
                                const holder = tasks.find(tk => tk.id === currentTaskId)
                                  ?.blocks?.find(b => b.id === usage.inBlockId);
                                const HolderIcon = holder ? BLOCK_META[holder.type]?.Icon : undefined;
                                /*
                                 * A CHIP IN THE EXIT IS NOT A BLOCK, and saying so is the whole
                                 * fix. The lookup above searches `task.blocks`, an exit block is
                                 * not in it, and the fallback called the chip "Untitled block" —
                                 * a name for a thing that has no name, where the truth is that
                                 * it is not that kind of thing at all.
                                 *
                                 * Keyed and deduped by SLOT rather than by block, so an exit
                                 * holding three paragraphs that each quote the same answer is
                                 * one chip saying where, not three saying it three times.
                                 *
                                 * Not a button: the others open a block on this page, and there
                                 * is nothing here to open — the exit is edited from the Flow
                                 * panel. Pressing the old one called `onOpenBlock` with an id
                                 * that is in no task, which did nothing at all.
                                 */
                                if (usage.inExitSlot) return (
                                  <li key={usage.inExitSlot}>
                                    <span className='flex items-center gap-1 rounded-md bg-ws-primary/[.08] px-2 py-1 text-xs text-ws-text-link dark:bg-ws-text-link/[.12]'>
                                      <MessageSquareText aria-hidden='true' className='h-3 w-3 shrink-0' />
                                      {t('blocks.answerRef.manager.inExit')}
                                    </span>
                                  </li>
                                );
                                return (
                                  <li key={usage.inBlockId}>
                                    <button
                                      type='button'
                                      onClick={() => onOpenBlock(usage.inBlockId)}
                                      className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs ${broken
                                        ? 'bg-ws-error-fg/[.10] text-ws-error-fg hover:bg-ws-error-fg/[.18]'
                                        : 'bg-ws-primary/[.08] text-ws-text-link hover:bg-ws-primary/[.14] dark:bg-ws-text-link/[.12] dark:hover:bg-ws-text-link/[.18]'}`}
                                    >
                                      {/* The block's own type icon normally, the error symbol
                                          when the reference is dead here: a tint on its own
                                          does not reach a colour-blind teacher. */}
                                      {broken
                                        ? <OctagonAlert aria-hidden='true' className='h-3 w-3 shrink-0' />
                                        : HolderIcon && <HolderIcon aria-hidden='true' className='h-3 w-3' />}
                                      {holder?.name ?? t('blocks.answerRef.unnamedSource')}
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}

                          {/*
                            * A HEADING RATHER THAN AN ICON ON EACH CHIP.
                            *
                            * This app has no symbol for a task, and the arrow that was here
                            * read as one — an arrow means the thing ACTS on the target, the way
                            * Flow uses it for a destination. These chips only say where a name
                            * is also used. A word above the list says that once.
                            */}
                          {onOpenTask && otherTasks.length > 0 && (
                            <div>
                              <p className='mb-1 text-xs text-ws-muted'>
                                {t('blocks.answerRef.manager.alsoUsedIn')}
                              </p>
                              <ul className='flex flex-wrap gap-1.5'>
                                {otherTasks.map(holder => {
                                  const breaksThere = row.breaksOn.includes(holder.title ?? '');
                                  return (
                                    <li key={holder.id}>
                                      <button
                                        type='button'
                                        onClick={() => onOpenTask(holder.id)}
                                        className={`flex items-center gap-1 rounded-md px-2 py-1 text-xs ${breaksThere
                                          ? 'bg-ws-error-fg/[.10] text-ws-error-fg hover:bg-ws-error-fg/[.18]'
                                          : 'bg-ws-primary/[.08] text-ws-text-link hover:bg-ws-primary/[.14] dark:bg-ws-text-link/[.12] dark:hover:bg-ws-text-link/[.18]'}`}
                                      >
                                        {/* THE SYMBOL CARRIES IT, not the tint. Colour alone
                                            does not reach a colour-blind teacher, and this is
                                            the one chip in the list worth crossing the panel
                                            for. `OctagonAlert` because the reference is an
                                            ERROR, which is what the octagon means everywhere
                                            in this app — the triangle is a warning. */}
                                        {breaksThere && (
                                          <OctagonAlert aria-hidden='true' className='h-3 w-3 shrink-0' />
                                        )}
                                        {holder.title || t('blocks.answerRef.manager.untitledTask')}
                                        {breaksThere && (
                                          <span className='sr-only'>{` ${t('blocks.answerRef.manager.breaksHere')}`}</span>
                                        )}
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}

                          {/*
                            * THE ONE ROW WITH NOWHERE ELSE TO PUT RE-POINT.
                            *
                            * A chip whose alias is gone, or one authored before aliases
                            * existed, has no addresses — so there is no line for a per-address
                            * control to sit on, and this is the only thing that can repair it.
                            */}
                          {!row.alias && (
                            <AnswerRefPickerMenu
                              tasks={tasks}
                              currentTaskId={currentTaskId}
                              onChoose={to => onRepoint(row.aliasId, to)}
                              ariaLabel={t('blocks.answerRef.manager.reanchorLabel', { name })}
                              renderTrigger={trigger => (
                                <button
                                  {...trigger}
                                  type='button'
                                  aria-label={t('blocks.answerRef.manager.reanchorLabel', { name })}
                                  className='self-start text-xs text-ws-text-link hover:underline'
                                >
                                  {t('blocks.answerRef.manager.reanchor')}
                                </button>
                              )}
                            />
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
